import React, { ReactNode } from "react";
import { Button, ButtonGroup, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import styled from "styled-components";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
    ChevronDoubleDown as HideIcon,
    ChevronDoubleUp as ShowIcon
} from "react-bootstrap-icons";

import HistoryCarousel from "./lanebuilder/HistoryCarousel";
import SeedInfo from "./SeedInfo";
import { getUserAnalyticsWidget, getUserHistory } from "../utils/Requests";
import Logger from "../utils/Logger";
import Branding from "../config/Branding";
import { THE_FILTER_FULL_AUTH } from "../constants";
import { CentralCol } from "../components/SharedStyled";

const ToggleButton = styled(Button)`
    position: absolute;
    z-index: 100;
    height: 30px;
    width: 30px;
    padding: 0;
    left: 15px;
    top: -15px;
`;

const HeaderRow = styled(Row)`
    padding-top: 5px;
    padding-bottom: 5px;
`;

const StickyBottomRow = styled(Row)`
    background: ${Branding.black};
    position: sticky;
    bottom: 0px;
    z-index: 200;
    border-top: thin solid gray;
    padding-bottom: 10px;
`;

const SpinnerDiv = styled.div`
    position: absolute;
    top: 39%;
    right: 50%;
    z-index: 10;
`;

const SeedPanelLoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 220px;
`;

const SeedPanelEmptyState = styled.div`
    min-height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
`;

const GenreLegendList = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px 14px;
    margin-top: 8px;
    font-size: 12px;
`;

const GenreLegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const GenreLegendSwatch = styled.span<{ $color: string }>`
    width: 10px;
    height: 10px;
    border-radius: 2px;
    background: ${(props) => props.$color};
    display: inline-block;
`;

const TopContentTable = styled(Table)`
    background: #121212;
    color: #f5f5f5;
    margin-bottom: 0;
    font-size: 14px;
    line-height: 1.4;

    thead th {
        background: #0a0a0a;
        color: #ffffff;
        border-color: #2b2b2b;
    }

    tbody td {
        border-color: #2b2b2b;
        padding: 0.14rem 0.3rem;
        white-space: nowrap;
    }

    thead th {
        padding: 0.18rem 0.3rem;
        white-space: nowrap;
    }

    tbody tr:nth-child(odd) {
        background: #171717;
    }

    tbody tr:nth-child(even) {
        background: #1f1f1f;
    }

    tbody tr:hover {
        background: #2a2a2a;
    }
`;

const MetricsMessage = styled.small`
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #cccccc;
    text-align: center;
`;

const RESP: any = ResponsiveContainer as any;
const BC: any = BarChart as any;
const BR: any = Bar as any;
const CG: any = CartesianGrid as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;
const CL: any = Cell as any;

const BAR_COLORS = ["#007bff", "#28a745", "#ffc107", "#17a2b8", "#dc3545", "#6f42c1", "#20c997", "#fd7e14", "#6610f2"];

type SeedAndUserHistoryPanelProperties = {
    customerConfig: CustomerConfig,
    userId: string,
    groups?: string[],
    seedItem?: Record<string, any>,
    showPanel: boolean,
    showHidePanelCallback: () => void
}

type SeedAndUserHistoryPanelState = {
    userHistory: Array<Record<string, any>>,
    loading: boolean,
    loaded: boolean,
    brandView: boolean,
    historyPanelView: "carousel" | "dashboard",
    genreBreakdown: Array<{ genre: string, count: number }>,
    genreBreakdownLoading: boolean,
    genreBreakdownLoaded: boolean,
    topContent: Array<{ brandId: string, name: string, playCount: number }>,
    topContentLoading: boolean,
    topContentLoaded: boolean
}

type GenreBreakdownApiItem = {
    genre: string,
    [key: string]: string | number
}

type TopContentApiItem = {
    [key: string]: string | number
}

export default class SeedAndUserHistoryPanel extends React.Component<SeedAndUserHistoryPanelProperties, SeedAndUserHistoryPanelState> {

    constructor(props: SeedAndUserHistoryPanelProperties) {
        super(props);

        this.state = {
            userHistory: [],
            loading: false,
            loaded: false,
            brandView: true,
            historyPanelView: "carousel",
            genreBreakdown: [],
            genreBreakdownLoading: false,
            genreBreakdownLoaded: false,
            topContent: [],
            topContentLoading: false,
            topContentLoaded: false
        };
    }

    public componentDidMount(): void {
        if (this.props.userId !== undefined && this.props.userId.length > 0) {
            this.requestUserHistory();
        }
    }

    public componentDidUpdate(prevProps: Readonly<SeedAndUserHistoryPanelProperties>): void {
        if (this.state.historyPanelView === "dashboard" && !this.hasDashboardAccess()) {
            this.setState({ historyPanelView: "carousel" });
            return;
        }

        if (this.props.userId !== undefined && this.props.userId.length > 0 && prevProps.userId !== this.props.userId) {
            if (this.state.historyPanelView === "dashboard") {
                this.requestGenreBreakdown();
                this.requestTopContent();
            } else {
                this.requestUserHistory();
            }
        }
    }

    private async requestUserHistory(): Promise<void> {
        this.setState({
            loading: true,
            loaded: false,
            userHistory: [],
            genreBreakdown: [],
            genreBreakdownLoading: false,
            genreBreakdownLoaded: false,
            topContent: [],
            topContentLoading: false,
            topContentLoaded: false
        });

        await getUserHistory(this.props.customerConfig!, this.props.userId, this.state.brandView).then(response => {
            if (response.ok) {
                return response.json();
            } else {
                this.setState({
                    userHistory: [],
                    loading: false,
                    loaded: true
                });
            }
        }).then(jsonOutput => {
            let historyItems = [];
            historyItems = jsonOutput.history;

            this.setState({
                userHistory: historyItems,
                loading: false,
                loaded: true
            });
        }).catch(error => {
            this.setState({
                userHistory: [],
                loading: false,
                loaded: true
            }, () => Logger.log(error));
        });
    }

    private changeView(): void {
        this.setState({
            brandView: !this.state.brandView
        }, this.requestUserHistory);
    }

    private requestGenreBreakdown(): void {
        this.setState({
            genreBreakdownLoading: true,
            genreBreakdownLoaded: false,
            genreBreakdown: []
        });

        getUserAnalyticsWidget(
            this.props.customerConfig,
            "user_history_genre_breakdown",
            this.props.userId
        ).then((response) => {
            const analyticsResponse = response as unknown as { result?: GenreBreakdownApiItem[] };
            const responseItems = Array.isArray(analyticsResponse?.result)
                ? analyticsResponse.result
                : [];

            const mappedGenreBreakdown = responseItems.map((item) => {
                return {
                    genre: item.genre,
                    count: Number(item["genre_count"] || 0)
                };
            });

            this.setState({
                genreBreakdown: mappedGenreBreakdown,
                genreBreakdownLoading: false,
                genreBreakdownLoaded: true
            });
        }).catch((error) => {
            Logger.log("user_history_genre_breakdown request failed", error);

            this.setState({
                genreBreakdown: [],
                genreBreakdownLoading: false,
                genreBreakdownLoaded: true
            });
        });
    }

    private requestTopContent(): void {
        this.setState({
            topContent: [],
            topContentLoading: true,
            topContentLoaded: true
        });

        getUserAnalyticsWidget(
            this.props.customerConfig,
            "user_top_content",
            this.props.userId
        ).then((response) => {
            const analyticsResponse = response as unknown as { result?: TopContentApiItem[] };
            const responseItems = Array.isArray(analyticsResponse?.result)
                ? analyticsResponse.result
                : [];

            const mappedTopContent = responseItems.map((item) => {
                return {
                    brandId: String(item["brand_id"] || ""),
                    name: String(item["thing_name"]).length > 50 ? `${String(item["thing_name"]).slice(0, 50)}…` : String(item["thing_name"]),
                    playCount: Number(item["plays"] || 0)
                };
            }).slice(0, 10);

            this.setState({
                topContent: mappedTopContent,
                topContentLoading: false,
                topContentLoaded: true
            });
        }).catch((error) => {
            Logger.log("user_top_content request failed", error);

            this.setState({
                topContentLoading: false,
                topContentLoaded: true
            });
        });
    }

    private changeHistoryPanelView(historyPanelView: "carousel" | "dashboard"): void {
        if (historyPanelView === "dashboard" && !this.hasDashboardAccess()) {
            this.setState({ historyPanelView: "carousel" });
            return;
        }

        this.setState({ historyPanelView }, () => {
            if (historyPanelView === "dashboard" && !this.state.genreBreakdownLoaded && !this.state.genreBreakdownLoading) {
                this.requestGenreBreakdown();
            }
            if (historyPanelView === "dashboard" && !this.state.topContentLoaded && !this.state.topContentLoading) {
                this.requestTopContent();
            }
        });
    }

    private hasDashboardAccess(): boolean {
        return !!this.props.groups?.includes(THE_FILTER_FULL_AUTH);
    }

    public render(): ReactNode {
        const hasDashboardAccess = this.hasDashboardAccess();
        const height = this.props.showPanel ? "360px" : "35px";
        const minHeight =  this.props.showPanel ? "360px" : "35px";
        const totalGenreCount = this.state.genreBreakdown.reduce((sum, item) => sum + item.count, 0);
        const renderGenreTooltip = ({ active, payload, label }: any): ReactNode => {
            if (!active || !payload || payload.length === 0) {
                return null;
            }

            const value = Number(payload[0].value || 0);
            const percentage = totalGenreCount > 0 ? (value / totalGenreCount) * 100 : 0;

            return (
                <div style={{ background: "#121212", border: "1px solid #2b2b2b", color: "#f5f5f5", padding: "8px" }}>
                    {`${label}: ${value} views (${percentage.toFixed(1)}%)`}
                </div>
            );
        };

        return (
            <StickyBottomRow style={{height, minHeight}}>
                <ToggleButton onClick={this.props.showHidePanelCallback}>
                    {this.props.showPanel ? <HideIcon /> : <ShowIcon />}
                </ToggleButton>
                {this.props.showPanel ?
                    <>
                        <>
                        <CentralCol md={2}>
                            <HeaderRow className="justify-content-center">
                                <big><b>Seed</b></big>
                            </HeaderRow>
                            {this.props.seedItem ?
                                <SeedInfo customerConfig={this.props.customerConfig} seedItem={this.props.seedItem} hideBody />
                                :
                                <p>No Seed selected</p>
                            }
                            <Row className="justify-content-center" style={{ marginTop: "8px" }}>
                                {hasDashboardAccess &&
                                <>    
                                    <ButtonGroup size="sm">
                                        <Button
                                            variant={this.state.historyPanelView === "carousel" ? "primary" : "outline-light"}
                                            onClick={() => this.changeHistoryPanelView("carousel")}
                                        >
                                            History
                                        </Button>      
                                        <Button
                                            variant={this.state.historyPanelView === "dashboard" ? "primary" : "outline-light"}
                                            onClick={() => this.changeHistoryPanelView("dashboard")}
                                        >
                                            Content
                                        </Button>
                                    </ButtonGroup>
                                    {this.state.historyPanelView === "dashboard" && <MetricsMessage>Metrics are based on the last 180 days of activity</MetricsMessage>}
                                </>
                                }
                            </Row>
                        </CentralCol>
                        <CentralCol md={10}>
                            {this.state.historyPanelView === "carousel" &&
                                <Row className="align-items-center">
                                    <Col md={3}>
                                        <Row className="align-items-center" style={{ paddingTop: "5px", paddingBottom: "5px" }}>
                                            <Form.Switch
                                                className="brandSwitch"
                                                onChange={() => {this.changeView();}}
                                                id="brand-switch"
                                                checked={this.state.brandView}
                                                label={this.state.brandView ? "Brand View" : "Episode View"}
                                            />
                                        </Row>
                                    </Col>
                                    <Col md={6}>
                                        <HeaderRow className="justify-content-center">
                                            <big><b>User History (Most recent 250 plays)</b></big>
                                        </HeaderRow>
                                    </Col>
                                    <Col md={3}>
                                    </Col>
                                </Row>
                            }
                            {this.state.loading ?
                                <>
                                <SpinnerDiv>
                                    <Spinner animation="border" />
                                </SpinnerDiv>
                                </>
                                :
                                <>
                                    {this.state.historyPanelView === "carousel" &&
                                        <Row className="justify-content-center">
                                            <Col>
                                            {this.state.userHistory.length > 0 &&
                                                <HistoryCarousel
                                                    items={this.state.userHistory}
                                                    loading={this.state.loading}
                                                    loaded={this.state.loaded}
                                                    brandView={this.state.brandView}
                                                />
                                            }
                                            {this.state.userHistory.length === 0 && this.state.loaded &&
                                                <p>No User History found</p>
                                            }
                                            </Col>
                                        </Row>
                                    }
                                    {this.state.historyPanelView === "dashboard" &&
                                        <>
                                            {(this.state.genreBreakdownLoading || this.state.topContentLoading)
                                                && this.state.genreBreakdown.length === 0
                                                && this.state.topContent.length === 0
                                                &&
                                                <SeedPanelLoadingWrapper>
                                                    <Spinner animation="border" />
                                                </SeedPanelLoadingWrapper>
                                            }
                                            {(this.state.genreBreakdown.length > 0 || this.state.topContent.length > 0) &&
                                                <Row>
                                                    <Col md={6}>
                                                        <HeaderRow className="justify-content-center">
                                                            <big><b>Genre Breakdown</b></big>
                                                        </HeaderRow>
                                                        {this.state.genreBreakdown.length > 0 &&
                                                            <>
                                                                <RESP width="100%" height={200}>
                                                                    <BC
                                                                        data={this.state.genreBreakdown}
                                                                        margin={{ top: 5, right: 5, left: 5, bottom: 10 }}
                                                                    >
                                                                        <CG strokeDasharray="3 3" />
                                                                        <XA dataKey="genre" hide />
                                                                        <YA label={{ value: "Views", angle: -90, position: "insideLeft" }} />
                                                                        <TT
                                                                            content={renderGenreTooltip}
                                                                        />
                                                                        <LG />
                                                                        <BR dataKey="count" name="Genre">
                                                                            {this.state.genreBreakdown.map((entry, index) => (
                                                                                <CL key={entry.genre} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                                                                            ))}
                                                                        </BR>
                                                                    </BC>
                                                                </RESP>
                                                                <GenreLegendList>
                                                                    {this.state.genreBreakdown.map((entry, index) => (
                                                                        <GenreLegendItem key={`legend-${entry.genre}`}>
                                                                            <GenreLegendSwatch $color={BAR_COLORS[index % BAR_COLORS.length]} />
                                                                            <span>{entry.genre}</span>
                                                                        </GenreLegendItem>
                                                                    ))}
                                                                </GenreLegendList>
                                                            </>
                                                        }
                                                        {this.state.genreBreakdownLoaded && this.state.genreBreakdown.length === 0 &&
                                                            <small>No genre breakdown data available</small>
                                                        }
                                                    </Col>
                                                    <Col md={6}>
                                                        <HeaderRow className="justify-content-center">
                                                            <big><b>Top Brands (Top 10)</b></big>
                                                        </HeaderRow>
                                                        {this.state.topContentLoaded && this.state.topContent.length > 0 &&
                                                            <TopContentTable bordered hover size="sm">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Brand ID</th>
                                                                        <th>Name</th>
                                                                        <th>Play Count</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {this.state.topContent.map((item, index) => (
                                                                        <tr key={`top-content-${item.brandId}-${index}`}>
                                                                            <td>{item.brandId}</td>
                                                                            <td>{item.name}</td>
                                                                            <td>{item.playCount}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </TopContentTable>
                                                        }
                                                        {this.state.topContentLoaded && this.state.topContent.length === 0 &&
                                                            <small>No top content data available</small>
                                                        }
                                                    </Col>
                                                </Row>
                                            }
                                            {this.state.genreBreakdownLoaded && this.state.topContentLoaded && this.state.genreBreakdown.length === 0 && this.state.topContent.length === 0 &&
                                                <SeedPanelEmptyState>
                                                    <small>No dashboard data available</small>
                                                </SeedPanelEmptyState>
                                            }
                                        </>
                                    }
                                </>
                            }
                        </CentralCol>
                        </>
                    </>
                    :
                    <CentralCol>
                        <Button variant="link" onClick={this.props.showHidePanelCallback}>Show seed information & user history</Button>
                    </CentralCol>
                }
            </StickyBottomRow>
        );
    }
}
